import logger from '../utils/logger';
import { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { Link } from 'react-router-dom';
import API_URL from '../utils/api';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle, DialogTrigger } from '../components/ui/dialog';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { toast } from 'sonner';
import {
    TrendingUp, TrendingDown, IndianRupee, Heart, Target as TargetIcon, AlertTriangle,
    GraduationCap, Activity, Users, Trophy, Sparkles, Cake, ChevronRight, RefreshCw,
    Settings, Mail, Send, Loader2, X, CheckCircle2,
} from 'lucide-react';

const fmtINR = (n) => `₹${(n || 0).toLocaleString('en-IN')}`;
const fmtCr = (n) => n >= 10000000 ? `₹${(n / 10000000).toFixed(2)}Cr` : n >= 100000 ? `₹${(n / 100000).toFixed(1)}L` : fmtINR(n);

const DigestSettingsDialog = ({ open, onOpenChange }) => {
    const [settings, setSettings] = useState(null);
    const [loading, setLoading] = useState(false);
    const [emailsRaw, setEmailsRaw] = useState('');
    const [saving, setSaving] = useState(false);
    const [sendingMe, setSendingMe] = useState(false);
    const [sendingNow, setSendingNow] = useState(false);

    const loadSettings = useCallback(async () => {
        setLoading(true);
        try {
            const { data } = await axios.get(`${API_URL}/api/exec/digest-settings`, { withCredentials: true });
            setSettings(data);
            setEmailsRaw((data.recipients || []).join(', '));
        } catch (e) {
            logger.error('digest-settings-load', e);
            toast.error('Failed to load digest settings');
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => { if (open) loadSettings(); }, [open, loadSettings]);

    const parseEmails = () => emailsRaw.split(/[,\n;]+/).map(s => s.trim()).filter(s => s && s.includes('@'));

    const save = async () => {
        const recipients = parseEmails();
        if (recipients.length === 0) { toast.error('Add at least one valid email'); return; }
        setSaving(true);
        try {
            await axios.put(`${API_URL}/api/exec/digest-settings`, { recipients }, { withCredentials: true });
            toast.success(`Saved ${recipients.length} recipient${recipients.length === 1 ? '' : 's'}`);
            await loadSettings();
        } catch (e) {
            logger.error('digest-settings-save', e);
            toast.error(e?.response?.data?.detail || 'Failed to save');
        } finally { setSaving(false); }
    };

    const sendToMe = async () => {
        setSendingMe(true);
        try {
            const { data } = await axios.post(`${API_URL}/api/exec/digest-settings/send-to-me`, {}, { withCredentials: true });
            toast.success(`Preview sent to ${data.to}`);
        } catch (e) {
            logger.error('digest-send-to-me', e);
            toast.error(e?.response?.data?.detail || 'Failed to send preview');
        } finally { setSendingMe(false); }
    };

    const sendNow = async () => {
        setSendingNow(true);
        try {
            const { data } = await axios.post(`${API_URL}/api/exec/digest-settings/send-now`, {}, { withCredentials: true });
            const failed = (data.failed || []).length;
            if (data.sent > 0) toast.success(`Digest sent to ${data.sent} recipient${data.sent === 1 ? '' : 's'}${failed ? ` · ${failed} failed` : ''}`);
            else toast.error(data.error || 'Digest failed to send');
            await loadSettings();
        } catch (e) {
            logger.error('digest-send-now', e);
            toast.error(e?.response?.data?.detail || 'Failed to send digest');
        } finally { setSendingNow(false); }
    };

    const lastSent = settings?.last_sent_at ? new Date(settings.last_sent_at).toLocaleString() : 'Never';

    return (
        <Dialog open={open} onOpenChange={onOpenChange}>
            <DialogContent className="max-w-lg" data-testid="digest-settings-dialog">
                <DialogHeader>
                    <DialogTitle className="flex items-center gap-2 text-slate-900" style={{ fontFamily: 'Chivo, sans-serif' }}>
                        <Mail className="h-5 w-5 text-[#2563eb]" /> Daily Briefing Email
                    </DialogTitle>
                    <DialogDescription>
                        Configure the recipient list for the GPT-5.2 morning briefing.
                        Auto-sends every day at <strong>08:05 IST</strong>.
                    </DialogDescription>
                </DialogHeader>

                {loading && !settings ? (
                    <div className="py-8 text-center text-sm text-slate-400">
                        <Loader2 className="h-5 w-5 animate-spin inline mr-2" /> Loading...
                    </div>
                ) : (
                    <div className="space-y-4">
                        {/* Schedule + last-sent strip */}
                        <div className="grid grid-cols-3 gap-2 text-xs">
                            <div className="bg-slate-50 rounded-lg p-2.5 border border-slate-100">
                                <p className="text-[10px] uppercase tracking-wider text-slate-500">Schedule</p>
                                <p className="font-bold text-slate-900 mt-0.5">{settings?.schedule || '08:05 IST'}</p>
                            </div>
                            <div className="bg-slate-50 rounded-lg p-2.5 border border-slate-100">
                                <p className="text-[10px] uppercase tracking-wider text-slate-500">Last sent</p>
                                <p className="font-bold text-slate-900 mt-0.5 truncate" data-testid="digest-last-sent">{lastSent}</p>
                            </div>
                            <div className="bg-slate-50 rounded-lg p-2.5 border border-slate-100">
                                <p className="text-[10px] uppercase tracking-wider text-slate-500">Last count</p>
                                <p className="font-bold text-slate-900 mt-0.5" data-testid="digest-last-count">{settings?.last_sent_count || 0}</p>
                            </div>
                        </div>

                        {/* Recipients editor */}
                        <div>
                            <Label className="text-xs font-bold uppercase tracking-wider text-slate-600">Recipients</Label>
                            <p className="text-[11px] text-slate-500 mb-2">Comma-, semicolon-, or newline-separated. Default: <code className="bg-slate-100 px-1 rounded">hr@digi-loan.com</code></p>
                            <textarea
                                value={emailsRaw}
                                onChange={(e) => setEmailsRaw(e.target.value)}
                                rows={3}
                                placeholder="hr@digi-loan.com, ceo@digi-loan.com"
                                className="w-full text-sm rounded-lg border border-slate-200 bg-slate-50 px-3 py-2 focus:bg-white focus:border-[#2563eb] focus:outline-none focus:ring-1 focus:ring-[#2563eb] transition-colors font-mono"
                                data-testid="digest-recipients-input"
                            />
                            {settings?.last_failed?.length > 0 && (
                                <p className="text-[11px] text-red-600 mt-1.5 flex items-center gap-1">
                                    <X className="h-3 w-3" /> Last failed: {settings.last_failed.join(', ')}
                                </p>
                            )}
                        </div>
                    </div>
                )}

                <DialogFooter className="gap-2 flex-wrap sm:flex-nowrap">
                    <Button variant="outline" size="sm" className="rounded-lg" onClick={sendToMe} disabled={sendingMe || loading} data-testid="digest-send-to-me-btn">
                        {sendingMe ? <Loader2 className="h-4 w-4 animate-spin mr-1.5" /> : <Send className="h-4 w-4 mr-1.5" />}
                        Send to me
                    </Button>
                    <Button variant="outline" size="sm" className="rounded-lg border-amber-300 text-amber-700 hover:bg-amber-50" onClick={sendNow} disabled={sendingNow || loading} data-testid="digest-send-now-btn">
                        {sendingNow ? <Loader2 className="h-4 w-4 animate-spin mr-1.5" /> : <CheckCircle2 className="h-4 w-4 mr-1.5" />}
                        Send now to all
                    </Button>
                    <Button size="sm" className="rounded-lg bg-[#2563eb] hover:bg-[#1d4ed8]" onClick={save} disabled={saving || loading} data-testid="digest-save-btn">
                        {saving ? <Loader2 className="h-4 w-4 animate-spin mr-1.5" /> : null}
                        Save recipients
                    </Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
};

const ExecutiveView = () => {
    const [snap, setSnap] = useState(null);
    const [loading, setLoading] = useState(true);
    const [refreshedAt, setRefreshedAt] = useState(null);
    const [narrative, setNarrative] = useState(null);
    const [narrativeLoading, setNarrativeLoading] = useState(true);
    const [digestOpen, setDigestOpen] = useState(false);

    const fetch = useCallback(async () => {
        setLoading(true);
        try {
            const { data } = await axios.get(`${API_URL}/api/exec/snapshot`, { withCredentials: true });
            setSnap(data);
            setRefreshedAt(new Date());
        } catch (e) { logger.error('exec-snapshot', e); }
        finally { setLoading(false); }
    }, []);

    const fetchNarrative = useCallback(async (refresh = false) => {
        setNarrativeLoading(true);
        try {
            const { data } = await axios.get(`${API_URL}/api/exec/narrative${refresh ? '?refresh=true' : ''}`, { withCredentials: true });
            setNarrative(data);
        } catch (e) { logger.error('exec-narrative', e); setNarrative({ narrative: 'Unable to generate narrative right now.', model: null, cache_hit: false }); }
        finally { setNarrativeLoading(false); }
    }, []);

    useEffect(() => { fetch(); fetchNarrative(); }, [fetch, fetchNarrative]);

    if (loading && !snap) {
        return <div className="p-12 text-center text-slate-400" data-testid="exec-loading">Loading executive snapshot...</div>;
    }
    if (!snap) {
        return <div className="p-12 text-center text-red-500">Unable to load snapshot.</div>;
    }

    const { revenue, top_rm, funnel, nps, goals, anomalies, engagement, learning, audit, hr_health, performance } = snap;

    return (
        <div className="space-y-6" data-testid="exec-view-page">
            {/* Header */}
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
                <div>
                    <p className="text-xs font-bold uppercase tracking-[0.3em] text-[#2563eb]">Executive · {snap.month_label}</p>
                    <h1 className="text-3xl font-black text-slate-900 tracking-tight" style={{ fontFamily: 'Chivo, sans-serif' }}>CEO View — One screen, 9 stories</h1>
                    {refreshedAt && <p className="text-xs text-slate-500 mt-1">Refreshed {refreshedAt.toLocaleTimeString()}</p>}
                </div>
                <Button variant="outline" className="rounded-lg" onClick={fetch} disabled={loading} data-testid="exec-refresh-btn">
                    <RefreshCw className={`h-4 w-4 mr-2 ${loading ? 'animate-spin' : ''}`} />Refresh
                </Button>
            </div>

            {/* This Week's Story — AI Narrative */}
            <Card className="rounded-2xl shadow-lg border-0 bg-gradient-to-br from-slate-900 via-slate-800 to-[#2563eb] text-white overflow-hidden relative" data-testid="exec-narrative-card">
                {/* Subtle grain texture overlay */}
                <div className="absolute inset-0 opacity-[0.06]" style={{ backgroundImage: 'radial-gradient(circle at 20% 30%, white 1px, transparent 1px), radial-gradient(circle at 80% 70%, white 1px, transparent 1px)', backgroundSize: '24px 24px' }} />
                <CardContent className="p-6 sm:p-8 relative">
                    <div className="flex items-start justify-between flex-wrap gap-3">
                        <div className="flex items-center gap-2.5">
                            <div className="p-2 bg-white/10 backdrop-blur-sm rounded-lg ring-1 ring-white/20">
                                <Sparkles className="h-4 w-4 text-amber-300" />
                            </div>
                            <div>
                                <p className="text-[10px] font-bold uppercase tracking-[0.3em] text-amber-300/90">This week's story · AI briefing</p>
                                <p className="text-[10px] text-white/50 mt-0.5">
                                    {narrative?.model === 'openai/gpt-5.2' ? 'Powered by GPT-5.2' : narrative?.model === 'fallback/deterministic' ? 'Stat-based fallback' : 'Generating...'}
                                    {narrative?.cache_hit && <span className="ml-2">· cached</span>}
                                </p>
                            </div>
                        </div>
                        <div className="flex items-center gap-1.5">
                            <Button variant="ghost" size="sm" className="rounded-lg text-white/70 hover:text-white hover:bg-white/10" onClick={() => fetchNarrative(true)} disabled={narrativeLoading} data-testid="narrative-refresh-btn">
                                <RefreshCw className={`h-3.5 w-3.5 mr-1.5 ${narrativeLoading ? 'animate-spin' : ''}`} />
                                {narrativeLoading ? 'Generating...' : 'Re-generate'}
                            </Button>
                            <Button variant="ghost" size="sm" className="rounded-lg text-white/70 hover:text-white hover:bg-white/10" onClick={() => setDigestOpen(true)} data-testid="narrative-settings-btn" title="Email digest settings">
                                <Settings className="h-3.5 w-3.5 mr-1.5" />
                                Email
                            </Button>
                        </div>
                    </div>

                    <div className="mt-5 space-y-3" data-testid="narrative-text">
                        {narrativeLoading && !narrative && (
                            <>
                                <div className="h-4 bg-white/10 rounded animate-pulse w-3/4" />
                                <div className="h-4 bg-white/10 rounded animate-pulse w-2/3" />
                                <div className="h-4 bg-white/10 rounded animate-pulse w-3/5" />
                            </>
                        )}
                        {narrative?.narrative && narrative.narrative.split('\n').filter(Boolean).map((line, i) => (
                            <p key={i} className="text-base sm:text-lg leading-relaxed text-white/95" style={{ fontFamily: 'Chivo, sans-serif', fontWeight: 400 }} data-testid={`narrative-line-${i}`}>
                                <span className="text-amber-300/70 font-bold mr-2 tabular-nums">{i + 1}.</span>{line.trim()}
                            </p>
                        ))}
                    </div>
                </CardContent>
            </Card>

            {/* TOP PERFORMER auto-generated PNG card */}
            <Card className="rounded-2xl shadow-md border-slate-200 overflow-hidden" data-testid="exec-top-performer-card">
                <div className="flex items-center justify-between px-5 py-3 border-b border-slate-100 bg-slate-50/50">
                    <div className="flex items-center gap-2">
                        <Trophy className="h-4 w-4 text-amber-500" />
                        <p className="text-xs font-bold uppercase tracking-[0.18em] text-slate-700">Today's Top Performer · Auto-shareable</p>
                    </div>
                    <a
                        href={`${API_URL}/api/exec/top-performer-card?period=TODAY&_=${Date.now()}`}
                        download={`top-performer-${new Date().toISOString().slice(0, 10)}.png`}
                        className="text-[11px] font-semibold text-[#2563eb] hover:text-[#1d4ed8] flex items-center gap-1"
                        data-testid="top-performer-download"
                    >
                        Download PNG <ChevronRight className="h-3 w-3" />
                    </a>
                </div>
                <div className="bg-slate-900 p-3">
                    <img
                        src={`${API_URL}/api/exec/top-performer-card?period=TODAY`}
                        alt="Today's Top Performer"
                        className="w-full h-auto rounded-lg shadow-xl"
                        data-testid="top-performer-img"
                    />
                </div>
                <div className="px-5 py-3 text-[11px] text-slate-500 bg-slate-50/50 border-t border-slate-100">
                    📩 This card is auto-attached to the daily 08:05 IST email digest. Shareable on WhatsApp / LinkedIn for recognition.
                </div>
            </Card>

            {/* HERO Row: Revenue + NPS + Goals */}
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
                {/* REVENUE */}
                <Link to="/crm-dashboard" className="block">
                    <Card className="rounded-2xl shadow-md border-0 bg-gradient-to-br from-[#2563eb] via-[#1d4ed8] to-[#16a34a] text-white overflow-hidden hover:shadow-xl transition-all hover:-translate-y-0.5" data-testid="exec-revenue-card">                        <CardContent className="p-6">
                            <div className="flex items-start justify-between mb-3">
                                <div className="flex items-center gap-2">
                                    <IndianRupee className="h-5 w-5" />
                                    <span className="text-xs font-bold uppercase tracking-[0.2em] opacity-80">Net Revenue · MTD</span>
                                </div>
                                <ChevronRight className="h-4 w-4 opacity-60" />
                            </div>
                            <p className="text-5xl font-extrabold tabular-nums leading-none" style={{ fontFamily: 'Chivo, sans-serif' }}>{fmtCr(revenue.net)}</p>
                            <div className="flex items-center gap-3 mt-4 text-xs">
                                <span className="bg-white/10 rounded-full px-2 py-0.5">{revenue.deals} deals</span>
                                <span className="bg-white/10 rounded-full px-2 py-0.5">Loans {fmtCr(revenue.loans)}</span>
                                {revenue.trend_pct !== null && (
                                    <span className={`flex items-center gap-1 rounded-full px-2 py-0.5 ${revenue.trend_pct >= 0 ? 'bg-emerald-500/30' : 'bg-red-500/30'}`}>
                                        {revenue.trend_pct >= 0 ? <TrendingUp className="h-3 w-3" /> : <TrendingDown className="h-3 w-3" />}
                                        {Math.abs(revenue.trend_pct)}% vs last month
                                    </span>
                                )}
                            </div>
                            {top_rm && (
                                <div className="mt-4 pt-4 border-t border-white/20">
                                    <p className="text-[10px] uppercase tracking-wider opacity-70">Top RM This Month</p>
                                    <p className="text-base font-bold mt-0.5">🏆 {top_rm.rm_name} · {fmtCr(top_rm.net_revenue)} · {top_rm.deals} deals</p>
                                </div>
                            )}
                        </CardContent>
                    </Card>
                </Link>

                {/* NPS */}
                <Link to="/crm-dashboard" className="block">
                    <Card className={`rounded-2xl shadow-md border-2 overflow-hidden hover:shadow-xl transition-all hover:-translate-y-0.5 ${nps.score >= 30 ? 'border-emerald-200 bg-gradient-to-br from-emerald-50 to-white' : nps.score >= 0 ? 'border-amber-200 bg-gradient-to-br from-amber-50 to-white' : 'border-red-200 bg-gradient-to-br from-red-50 to-white'}`} data-testid="exec-nps-card">
                        <CardContent className="p-6">
                            <div className="flex items-start justify-between mb-3">
                                <div className="flex items-center gap-2">
                                    <Heart className={`h-5 w-5 ${nps.score >= 30 ? 'text-emerald-600' : nps.score >= 0 ? 'text-amber-600' : 'text-red-600'}`} />
                                    <span className="text-xs font-bold uppercase tracking-[0.2em] text-slate-500">Customer NPS · 30d</span>
                                </div>
                                <ChevronRight className="h-4 w-4 text-slate-400" />
                            </div>
                            <p className={`text-5xl font-extrabold tabular-nums leading-none ${nps.score >= 30 ? 'text-emerald-600' : nps.score >= 0 ? 'text-amber-600' : 'text-red-600'}`} style={{ fontFamily: 'Chivo, sans-serif' }}>{nps.score >= 0 ? '+' : ''}{nps.score}</p>
                            <p className="text-xs text-slate-500 mt-2 capitalize">{nps.verdict.replace(/_/g, ' ')} · avg {nps.avg_score}/10</p>
                            <div className="grid grid-cols-3 gap-2 mt-4 text-xs">
                                <div className="bg-emerald-50 rounded-lg p-2 text-center"><p className="text-emerald-700 font-bold tabular-nums">{nps.promoters}</p><p className="text-[10px] text-slate-500">Promoters</p></div>
                                <div className="bg-amber-50 rounded-lg p-2 text-center"><p className="text-amber-700 font-bold tabular-nums">{nps.passives ?? (nps.responses - nps.promoters - nps.detractors)}</p><p className="text-[10px] text-slate-500">Passives</p></div>
                                <div className="bg-red-50 rounded-lg p-2 text-center"><p className="text-red-700 font-bold tabular-nums">{nps.detractors}</p><p className="text-[10px] text-slate-500">Detractors</p></div>
                            </div>
                            <p className="text-[10px] text-slate-400 mt-3">{nps.responses} of {nps.sent} responded · {nps.response_rate_pct}% rate</p>
                        </CardContent>
                    </Card>
                </Link>

                {/* GOALS */}
                <Link to="/goals" className="block">
                    <Card className="rounded-2xl shadow-md border-2 border-slate-200 bg-gradient-to-br from-slate-50 to-white overflow-hidden hover:shadow-xl transition-all hover:-translate-y-0.5" data-testid="exec-goals-card">
                        <CardContent className="p-6">
                            <div className="flex items-start justify-between mb-3">
                                <div className="flex items-center gap-2">
                                    <TargetIcon className="h-5 w-5 text-[#2563eb]" />
                                    <span className="text-xs font-bold uppercase tracking-[0.2em] text-slate-500">Goals on pace</span>
                                </div>
                                <ChevronRight className="h-4 w-4 text-slate-400" />
                            </div>
                            <p className="text-5xl font-extrabold text-slate-900 tabular-nums leading-none" style={{ fontFamily: 'Chivo, sans-serif' }}>{goals.on_pace_pct}<span className="text-2xl">%</span></p>
                            <p className="text-xs text-slate-500 mt-2">{goals.on_pace} of {goals.total} on track</p>
                            <div className="mt-4 h-2 bg-slate-100 rounded-full overflow-hidden">
                                <div className="h-full bg-gradient-to-r from-[#2563eb] to-emerald-500 rounded-full transition-all" style={{ width: `${goals.on_pace_pct}%` }} />
                            </div>
                            <div className="flex items-center justify-between mt-4 text-xs">
                                <span className="text-emerald-600 font-medium">✓ {goals.completed} done</span>
                                <span className="text-amber-600 font-medium">⚠ {goals.at_risk} at risk</span>
                                <span className="text-slate-400">{goals.not_started} new</span>
                            </div>
                            <p className="text-[10px] text-slate-400 mt-3">Avg progress {goals.avg_progress_pct}%</p>
                        </CardContent>
                    </Card>
                </Link>
            </div>

            {/* Row 2: Anomalies + Engagement + Learning */}
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
                {/* ANOMALIES */}
                <Link to="/audit-log" className="block lg:col-span-1">
                    <Card className={`rounded-2xl shadow-sm border-2 overflow-hidden hover:shadow-md transition-all h-full ${anomalies.high_open > 0 ? 'border-red-200 bg-red-50/40' : anomalies.open_total > 0 ? 'border-amber-200 bg-amber-50/30' : 'border-emerald-200 bg-emerald-50/20'}`} data-testid="exec-anomalies-card">
                        <CardHeader className="pb-2 pt-4 px-5">
                            <CardTitle className="text-sm font-bold flex items-center justify-between" style={{ fontFamily: 'Chivo, sans-serif' }}>
                                <span className="flex items-center gap-2">
                                    <AlertTriangle className={`h-4 w-4 ${anomalies.high_open > 0 ? 'text-red-600' : 'text-amber-600'}`} />
                                    Anomalies · Top 3
                                </span>
                                {anomalies.open_total > 0 && <span className="text-[10px] font-bold uppercase tracking-wider px-2 py-0.5 rounded bg-red-600 text-white">{anomalies.open_total}</span>}
                            </CardTitle>
                        </CardHeader>
                        <CardContent className="p-3 pt-2">
                            {anomalies.top_3.length === 0 ? (
                                <p className="text-xs text-emerald-700 text-center py-6">✅ All clear</p>
                            ) : (
                                <ul className="space-y-2">
                                    {anomalies.top_3.map(a => (
                                        <li key={a._id} className="bg-white rounded-lg border border-slate-200 p-2.5 text-xs" data-testid={`exec-anomaly-${a._id}`}>
                                            <div className="flex items-start gap-2">
                                                <span className={`text-[9px] font-bold uppercase px-1.5 py-0.5 rounded mt-0.5 shrink-0 ${a.severity === 'high' ? 'bg-red-100 text-red-700' : a.severity === 'medium' ? 'bg-amber-100 text-amber-700' : 'bg-slate-100 text-slate-600'}`}>{a.severity}</span>
                                                <span className="flex-1 line-clamp-2">{a.description}</span>
                                            </div>
                                        </li>
                                    ))}
                                </ul>
                            )}
                        </CardContent>
                    </Card>
                </Link>

                {/* ENGAGEMENT */}
                <Link to="/engagement" className="block">
                    <Card className="rounded-2xl shadow-sm border-2 border-pink-200 bg-gradient-to-br from-pink-50 to-rose-50 overflow-hidden hover:shadow-md transition-all h-full" data-testid="exec-engagement-card">
                        <CardHeader className="pb-2 pt-4 px-5">
                            <CardTitle className="text-sm font-bold flex items-center gap-2" style={{ fontFamily: 'Chivo, sans-serif' }}>
                                <Heart className="h-4 w-4 text-pink-500" />
                                Engagement Pulse
                            </CardTitle>
                        </CardHeader>
                        <CardContent className="p-4 pt-2 space-y-3">
                            <div>
                                <p className="text-2xl font-extrabold text-slate-900 tabular-nums" style={{ fontFamily: 'Chivo, sans-serif' }}>{engagement.kudos_7d}</p>
                                <p className="text-[10px] uppercase tracking-wider text-slate-500">Kudos this week</p>
                            </div>
                            {engagement.top_kudos && (
                                <div className="bg-white border border-slate-200 rounded-lg p-2 text-xs">
                                    <p className="text-[10px] uppercase tracking-wider text-slate-400">🏆 Top recognized</p>
                                    <p className="font-bold text-slate-900 mt-0.5 truncate">{engagement.top_kudos.employee_name} · {engagement.top_kudos.kudos_count}</p>
                                </div>
                            )}
                            {engagement.active_pulse ? (
                                <div className="bg-white border border-slate-200 rounded-lg p-2 text-xs">
                                    <p className="text-[10px] uppercase tracking-wider text-slate-400 flex items-center gap-1"><Sparkles className="h-3 w-3" /> Active pulse</p>
                                    <p className="font-bold text-slate-900 truncate">{engagement.active_pulse.title}</p>
                                    <p className="text-slate-500">{engagement.active_pulse.responses}/{engagement.active_pulse.eligible} · {engagement.active_pulse.response_rate_pct}%</p>
                                </div>
                            ) : (
                                <p className="text-[10px] text-slate-400 italic">No active pulse</p>
                            )}
                            {engagement.upcoming_celebrations.length > 0 && (
                                <div className="text-xs">
                                    <p className="text-[10px] uppercase tracking-wider text-slate-400 flex items-center gap-1"><Cake className="h-3 w-3" /> Next 7 days</p>
                                    {engagement.upcoming_celebrations.slice(0, 3).map((c) => (
                                        <p key={`${c.name}-${c.kind}-${c.days_until}`} className="text-slate-600 mt-0.5"><strong>{c.name}</strong> · {c.kind} · <span className="text-slate-400">{c.days_until === 0 ? 'today' : `${c.days_until}d`}</span></p>
                                    ))}
                                </div>
                            )}
                        </CardContent>
                    </Card>
                </Link>

                {/* LEARNING */}
                <Link to="/learning" className="block">
                    <Card className="rounded-2xl shadow-sm border-2 border-blue-200 bg-gradient-to-br from-blue-50 to-white overflow-hidden hover:shadow-md transition-all h-full" data-testid="exec-learning-card">
                        <CardHeader className="pb-2 pt-4 px-5">
                            <CardTitle className="text-sm font-bold flex items-center gap-2" style={{ fontFamily: 'Chivo, sans-serif' }}>
                                <GraduationCap className="h-4 w-4 text-blue-600" />
                                Compliance & Learning
                            </CardTitle>
                        </CardHeader>
                        <CardContent className="p-4 pt-2 space-y-3">
                            <div>
                                <p className="text-3xl font-extrabold text-slate-900 tabular-nums" style={{ fontFamily: 'Chivo, sans-serif' }}>{learning.compliance_pct}<span className="text-lg">%</span></p>
                                <p className="text-[10px] uppercase tracking-wider text-slate-500">Mandatory compliance</p>
                                <div className="mt-2 h-1.5 bg-slate-100 rounded-full overflow-hidden">
                                    <div className={`h-full rounded-full ${learning.compliance_pct >= 80 ? 'bg-emerald-500' : learning.compliance_pct >= 60 ? 'bg-amber-500' : 'bg-red-500'}`} style={{ width: `${learning.compliance_pct}%` }} />
                                </div>
                            </div>
                            <div className="grid grid-cols-3 gap-2 text-xs">
                                <div><p className="font-bold tabular-nums text-slate-900">{learning.total_courses}</p><p className="text-[10px] text-slate-500">Courses</p></div>
                                <div><p className="font-bold tabular-nums text-emerald-600">{learning.certificates_30d}</p><p className="text-[10px] text-slate-500">Certs · 30d</p></div>
                                <div><p className="font-bold tabular-nums text-blue-600">{learning.active_enrollments}</p><p className="text-[10px] text-slate-500">In progress</p></div>
                            </div>
                        </CardContent>
                    </Card>
                </Link>
            </div>

            {/* Row 3: Performance + HR Health + Audit */}
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
                {/* PERFORMANCE */}
                <Link to="/crm-dashboard" className="block">
                    <Card className="rounded-2xl shadow-sm border-2 border-emerald-200 bg-gradient-to-br from-emerald-50 to-white overflow-hidden hover:shadow-md transition-all h-full" data-testid="exec-performance-card">
                        <CardHeader className="pb-2 pt-4 px-5">
                            <CardTitle className="text-sm font-bold flex items-center gap-2" style={{ fontFamily: 'Chivo, sans-serif' }}>
                                <Trophy className="h-4 w-4 text-emerald-600" />
                                RM Performance Mix
                            </CardTitle>
                        </CardHeader>
                        <CardContent className="p-4 pt-2 space-y-3">
                            <div className="grid grid-cols-4 gap-2 text-xs">
                                <div className="bg-emerald-100 rounded-lg p-2 text-center"><p className="text-2xl font-extrabold text-emerald-700 tabular-nums">{performance.top_performers}</p><p className="text-[9px] text-emerald-700 uppercase">Top</p></div>
                                <div className="bg-blue-100 rounded-lg p-2 text-center"><p className="text-2xl font-extrabold text-blue-700 tabular-nums">{performance.strong_rms}</p><p className="text-[9px] text-blue-700 uppercase">Strong</p></div>
                                <div className="bg-amber-100 rounded-lg p-2 text-center"><p className="text-2xl font-extrabold text-amber-700 tabular-nums">{performance.average}</p><p className="text-[9px] text-amber-700 uppercase">Average</p></div>
                                <div className="bg-red-100 rounded-lg p-2 text-center"><p className="text-2xl font-extrabold text-red-700 tabular-nums">{performance.low_performers}</p><p className="text-[9px] text-red-700 uppercase">Low</p></div>
                            </div>
                            {performance.top_3.length > 0 && (
                                <div>
                                    <p className="text-[10px] uppercase tracking-wider text-slate-400 mb-1">Top 3 by net revenue</p>
                                    <ol className="space-y-1">
                                        {performance.top_3.map((rm, i) => (
                                            <li key={rm.rm_id} className="flex items-center gap-2 text-xs">
                                                <span>{['🥇', '🥈', '🥉'][i]}</span>
                                                <span className="flex-1 truncate font-medium">{rm.rm_name}</span>
                                                <span className="text-slate-500 font-mono">{fmtCr(rm.net)}</span>
                                            </li>
                                        ))}
                                    </ol>
                                </div>
                            )}
                        </CardContent>
                    </Card>
                </Link>

                {/* HR HEALTH */}
                <Link to="/employees" className="block">
                    <Card className="rounded-2xl shadow-sm border-2 border-purple-200 bg-gradient-to-br from-purple-50 to-white overflow-hidden hover:shadow-md transition-all h-full" data-testid="exec-hr-card">
                        <CardHeader className="pb-2 pt-4 px-5">
                            <CardTitle className="text-sm font-bold flex items-center gap-2" style={{ fontFamily: 'Chivo, sans-serif' }}>
                                <Users className="h-4 w-4 text-purple-600" />
                                HR Health
                            </CardTitle>
                        </CardHeader>
                        <CardContent className="p-4 pt-2">
                            <div className="grid grid-cols-2 gap-3">
                                <div>
                                    <p className="text-3xl font-extrabold text-slate-900 tabular-nums" style={{ fontFamily: 'Chivo, sans-serif' }}>{hr_health.active_employees}</p>
                                    <p className="text-[10px] uppercase tracking-wider text-slate-500">Active employees</p>
                                </div>
                                <div>
                                    <p className="text-3xl font-extrabold text-purple-600 tabular-nums" style={{ fontFamily: 'Chivo, sans-serif' }}>{hr_health.attendance_pct_mtd}<span className="text-lg">%</span></p>
                                    <p className="text-[10px] uppercase tracking-wider text-slate-500">Attendance MTD</p>
                                </div>
                            </div>
                            <div className="mt-4 pt-3 border-t border-slate-100 grid grid-cols-3 gap-2 text-xs">
                                <div><p className="font-bold tabular-nums text-emerald-600">+{hr_health.new_hires_30d}</p><p className="text-[10px] text-slate-500">New · 30d</p></div>
                                <div><p className="font-bold tabular-nums text-amber-600">{hr_health.pending_leaves}</p><p className="text-[10px] text-slate-500">Leaves to approve</p></div>
                                <div><p className="font-bold tabular-nums text-slate-500">{hr_health.inactive_employees}</p><p className="text-[10px] text-slate-500">Inactive</p></div>
                            </div>
                        </CardContent>
                    </Card>
                </Link>

                {/* AUDIT */}
                <Link to="/audit-log" className="block">
                    <Card className="rounded-2xl shadow-sm border-2 border-slate-200 bg-gradient-to-br from-slate-50 to-white overflow-hidden hover:shadow-md transition-all h-full" data-testid="exec-audit-card">
                        <CardHeader className="pb-2 pt-4 px-5">
                            <CardTitle className="text-sm font-bold flex items-center gap-2" style={{ fontFamily: 'Chivo, sans-serif' }}>
                                <Activity className="h-4 w-4 text-slate-600" />
                                Audit Pulse · Today
                            </CardTitle>
                        </CardHeader>
                        <CardContent className="p-4 pt-2 space-y-3">
                            <div>
                                <p className="text-3xl font-extrabold text-slate-900 tabular-nums" style={{ fontFamily: 'Chivo, sans-serif' }}>{audit.mutations_today}</p>
                                <p className="text-[10px] uppercase tracking-wider text-slate-500">Mutations today</p>
                            </div>
                            <div className="grid grid-cols-2 gap-2 text-xs">
                                {audit.top_resource && (
                                    <div className="bg-white border border-slate-200 rounded-lg p-2">
                                        <p className="text-[10px] uppercase tracking-wider text-slate-400">Hot resource</p>
                                        <p className="font-bold text-slate-900 capitalize truncate">{audit.top_resource.replace(/_/g, ' ')}</p>
                                    </div>
                                )}
                                {audit.top_actor && (
                                    <div className="bg-white border border-slate-200 rounded-lg p-2">
                                        <p className="text-[10px] uppercase tracking-wider text-slate-400">Most active</p>
                                        <p className="font-bold text-slate-900 truncate">{audit.top_actor}</p>
                                    </div>
                                )}
                            </div>
                        </CardContent>
                    </Card>
                </Link>
            </div>

            {/* Funnel Strip */}
            <Card className="rounded-2xl shadow-sm border-slate-200 overflow-hidden" data-testid="exec-funnel">
                <CardContent className="p-4">
                    <p className="text-[10px] uppercase tracking-[0.3em] text-slate-400 mb-3">Lead Funnel · MTD</p>
                    <div className="grid grid-cols-5 gap-2">
                        {Object.entries(funnel).map(([k, v], i) => (
                            <div key={k} className="text-center">
                                <p className="text-2xl font-extrabold text-slate-900 tabular-nums" style={{ fontFamily: 'Chivo, sans-serif' }}>{v}</p>
                                <p className="text-[10px] uppercase tracking-wider text-slate-500 mt-0.5 capitalize">{k.replace(/_/g, ' ')}</p>
                            </div>
                        ))}
                    </div>
                </CardContent>
            </Card>

            <DigestSettingsDialog open={digestOpen} onOpenChange={setDigestOpen} />
        </div>
    );
};

export default ExecutiveView;
